import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import * as React from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import FilterChip from 'LuxKit/Chips/FilterChip'
import clsx from 'clsx'
import noop from 'lib/function/noop'
import { useLockedOptionsPreview } from './useLockedOptionsPreview'

const PillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

const pillStreamReveal = keyframes`
  from {
    opacity: 0;
    transform: translateY(${rem(6)}) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`

const OptionWrapper = styled.div`
  overflow: hidden;
  transition: max-width 220ms ease, max-height 220ms ease, opacity 220ms ease, transform 220ms ease;
  max-width: 100%;
  max-height: ${rem(48)};
  opacity: 1;
  transform: translateY(0) scale(1);

  &.is-hidden {
    max-width: 0;
    max-height: 0;
    opacity: 0;
    transform: translateY(${rem(-8)}) scale(0.96);
    pointer-events: none;
  }

  &.is-streaming {
    animation: ${pillStreamReveal} 180ms ease-out both;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &.is-streaming {
      animation: none;
    }
  }
`

const StyledFilterChip = styled(FilterChip)`
  &:disabled {
    cursor: default;
  }

  &.selected:disabled {
    --chip-stroke-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

export interface PillOption {
  label: string
  action?: string
}

interface Props {
  options: Array<PillOption>
  onSelectionChange?: (selectedLabels: Array<string>) => void
  onAction?: (action: string) => void
  select: 'single' | 'multiple'
  disabled?: boolean
  selection?: Array<string>
  isStreaming?: boolean
}

function PillsList({ options, onSelectionChange = noop, onAction, select, disabled = false, selection, isStreaming = false }: Props) {
  const intl = useIntl()
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(() => {
    const initialIndices = new Set<number>()
    if (selection) {
      selection.forEach(label => {
        const index = options.findIndex(opt => opt.label === label)
        if (index !== -1) {
          initialIndices.add(index)
        }
      })
    }
    return initialIndices
  })
  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const isInternalUpdateRef = useRef(false)
  const prevSelectionRef = useRef(selection)

  const validLabels = useMemo(
    () => new Set(options.map(opt => opt.label)),
    [options],
  )

  useEffect(() => {
    if (
      selection !== undefined &&
      !disabled &&
      !isInternalUpdateRef.current &&
      prevSelectionRef.current !== selection
    ) {
      const newIndices = new Set<number>()
      const usedIndices = new Set<number>()
      selection.forEach(label => {
        const index = options.findIndex((opt, idx) => opt.label === label && !usedIndices.has(idx))
        if (index !== -1) {
          newIndices.add(index)
          usedIndices.add(index)
        }
      })

      const currentArray = Array.from(selectedIndices)
      const hasChanges =
        newIndices.size !== currentArray.length ||
        currentArray.some(idx => !newIndices.has(idx))

      if (hasChanges) {
        setSelectedIndices(newIndices)
      }
    }
    prevSelectionRef.current = selection
    isInternalUpdateRef.current = false
  }, [selection, disabled, selectedIndices, options])

  const onActionRef = useRef(onAction)
  onActionRef.current = onAction

  const {
    isExpanded,
    shouldRenderAllOptions,
    shouldShowSummaryTile,
    collapsedSelectedIndices,
    handleExpand,
    handleCollapse,
    shouldHideOption,
  } = useLockedOptionsPreview({
    disabled,
    selectedIndices,
    optionsLength: options.length,
  })

  const collapsedOptions = useMemo(() => {
    return collapsedSelectedIndices.map(index => ({ option: options[index], index })).filter(
      (entry): entry is { option: PillOption; index: number } => entry.option !== undefined,
    )
  }, [collapsedSelectedIndices, options])

  const renderedOptions = useMemo(() => {
    if (shouldRenderAllOptions) {
      return options.map((option, index) => ({ option, index }))
    }

    return collapsedOptions
  }, [collapsedOptions, options, shouldRenderAllOptions])

  const showOtherOptionsLabel = useMemo(() => {
    return intl.formatMessage({
      id: 'RhNbYzYHjZnAx2qa8pqL',
      defaultMessage: 'Show other options',
      description: 'Locked-history pill label that expands unselected pill options in trip planner chat',
    })
  }, [intl])

  const showSelectedOnlyLabel = useMemo(() => {
    return intl.formatMessage({
      id: 'xQPkL7ydJx6XKv71mwqQ',
      defaultMessage: 'Show selected only',
      description: 'Locked-history pill label that collapses pill options back to selected entries in trip planner chat',
    })
  }, [intl])

  const hideOptionsLabel = useMemo(() => {
    return intl.formatMessage({
      id: 'RmZa65U-wl0kYCWqCnn4',
      defaultMessage: 'Hide options',
      description: 'Locked-history pill label that collapses pill options when there was no prior selection in trip planner chat',
    })
  }, [intl])

  const showOptionsLabel = useMemo(() => {
    return intl.formatMessage({
      id: 'EqEfSM9iAmDHUMOTcSsK',
      defaultMessage: 'Show options',
      description: 'Locked-history pill label that expands pill options when nothing was selected in trip planner chat',
    })
  }, [intl])

  const handleOptionToggle = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    const indexStr = event.currentTarget.dataset.index
    if (indexStr === undefined) return
    const index = parseInt(indexStr, 10)
    if (isNaN(index)) return

    const clickedOption = options[index]
    // Action pills toggle visual selection and fire onAction on every click
    // (select and deselect) so the consumer can toggle associated UI. They
    // skip onSelectionChange since they drive side-effects (e.g. showing an
    // inline form) rather than contributing to the multi-choice selection model.
    if (clickedOption?.action && onActionRef.current) {
      onActionRef.current(clickedOption.action)
      setSelectedIndices(prevSelected => {
        const newSelection = new Set(prevSelected)
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
        return newSelection
      })
      return
    }

    isInternalUpdateRef.current = true

    setSelectedIndices(prevSelected => {
      const newSelection = new Set(prevSelected)
      if (select === 'single') {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.clear()
          newSelection.add(index)
        }
      } else {
        if (newSelection.has(index)) {
          newSelection.delete(index)
        } else {
          newSelection.add(index)
        }
      }

      const selectedLabels = Array.from(newSelection)
        .filter(idx => !options[idx]?.action)
        .map(idx => options[idx]?.label)
        .filter(Boolean)

      const otherSelections = (selection ?? []).filter(s => !validLabels.has(s))
      const mergedSelection = [...otherSelections, ...selectedLabels]

      onSelectionChangeRef.current(mergedSelection)
      return newSelection
    })
  }, [disabled, select, selection, validLabels, options])

  if (options.length === 0) {
    return undefined
  }

  const hasSelection = selectedIndices.size > 0
  const allOptionsSelected = selectedIndices.size === options.length
  const toggleButtonLabel = shouldShowSummaryTile
    ? showOptionsLabel
    : isExpanded
      ? hasSelection ? showSelectedOnlyLabel : hideOptionsLabel
      : showOtherOptionsLabel
  const shouldShowToggleButton = disabled && (
    shouldShowSummaryTile ||
    isExpanded ||
    (hasSelection && !allOptionsSelected)
  )

  return (
    <PillsContainer>
      {!shouldShowSummaryTile && renderedOptions.map(({ option, index }) => {
        const isHidden = shouldHideOption(index)
        const isSelected = selectedIndices.has(index)
        return (
          <OptionWrapper
            key={`${option.label}-${index}`}
            className={clsx(isHidden && 'is-hidden', isStreaming && 'is-streaming')}
            data-testid={isStreaming ? 'pills-list-streaming-pill' : undefined}
          >
            <StyledFilterChip
              size="medium"
              selected={isSelected}
              disabled={disabled}
              onClick={handleOptionToggle}
              data-index={index}
              aria-label={option.label}
            >
              {option.label}
            </StyledFilterChip>
          </OptionWrapper>
        )
      })}
      {shouldShowToggleButton && (
        <StyledFilterChip
          size="medium"
          selected={false}
          onClick={isExpanded ? handleCollapse : handleExpand}
          aria-expanded={isExpanded}
          aria-label={toggleButtonLabel}
        >
          {toggleButtonLabel}
        </StyledFilterChip>
      )}
    </PillsContainer>
  )
}

export default PillsList
